import type { LivePresentation } from './config';

/**
 * All of the widget's CSS, built from the presentation config and injected into the shadow root.
 *
 * Nothing is ever written to document.head: the styles live and die with the shadow root, so the
 * widget cannot leak into the publisher's page and the publisher's stylesheet cannot reach in.
 * The wrapper resets inherited properties, because inheritance is the one thing shadow DOM lets
 * through.
 */

/**
 * The space held before the config arrives, and kept afterwards, so nothing on the page jumps.
 *
 * The widget cannot know its height until it has fetched its config, so the skeleton is always a
 * guess. Overshooting is invisible: the box only ever grows, so content that needs less simply
 * sits inside it. Undershooting drops everything below the embed down the article the moment the
 * config lands, which is a layout shift, and CLAUDE.md's fourth non-negotiable says there are not
 * supposed to be any.
 *
 * So these numbers are set from what interactions actually render at, measured in a browser across
 * a corpus of polls, quizzes, surveys and forms, rather than from one flat figure. Height is a
 * function of width, and steeply so: a four option poll is 328px tall in a 1248px column and 447px
 * tall in a 300px one, because a narrow column wraps every prompt and every option label.
 *
 * This is the fallback. A snippet published with a reserve hint carries the exact number for the
 * interaction it embeds and never comes near this table. The table is for embeds pasted into
 * articles before the hint existed, which are live and cannot be edited.
 *
 * Those embeds hold no space at all until this script runs, so one shift is already unavoidable
 * and its size is whatever the box ends up as. That is why these sit on the median of measured
 * heights rather than above it: reserving more than the content needs makes the unavoidable shift
 * larger rather than smaller, and it leaves a band of nothing under a short poll. Measured across
 * the corpus, the median is worth about 9% less shift than a flat 180 was, and it removes the
 * second jump (the visible one, where the content arrives and pushes the article down) for most
 * interactions rather than all of them getting it.
 *
 * Each row is `[minimum width, reserved height]`, widest first.
 */
const DEFAULT_RESERVE: readonly (readonly [number, number])[] = [
  [700, 330],
  [560, 360],
  [440, 380],
  [340, 410],
  [0, 440],
];

/** What to hold for a widget of this width when the embed carries no hint. */
export function defaultReserve(width: number): number {
  const usable = Number.isFinite(width) && width > 0 ? width : 700;
  for (const [from, px] of DEFAULT_RESERVE) {
    if (usable >= from) return px;
  }
  return 440;
}

/** Where a published snippet carries the exact reserve for the interaction it embeds. */
export const RESERVE_ATTRIBUTE = 'data-quizee-reserve';

/** Nothing sensible reserves more than this, and a typo should not blank out an article. */
const MAX_RESERVE_PX = 4000;

/**
 * Read the reserve hint the embed snippet carries.
 *
 * The format is a comma separated list of `maxWidth:px` pairs in ascending order, ending in `*:px`
 * for anything wider, which the API writes from the published content (see `reserveHint` in
 * `@quizee/core`). The first pair covering the measured width wins.
 *
 * Anything malformed returns null and the caller falls back to the width based default. This runs
 * on somebody else's page against an attribute anybody could have edited, so it is written to
 * refuse rather than to interpret.
 */
export function reserveFromHint(hint: string | null, width: number): number | null {
  if (hint === null || hint.length === 0 || hint.length > 200) return null;

  /**
   * An unmeasurable width means a wide column, not the narrowest one.
   *
   * An element inside a collapsed accordion, a hidden tab panel or anything `display: none`
   * measures zero. Matching zero against the first band picks the phone height, which is the
   * tallest, so the widget reserves far more than it needs and leaves a band of empty space when
   * the panel opens. `defaultReserve` already guards this the same way.
   */
  const usable = Number.isFinite(width) && width > 0 ? width : 700;

  for (const pair of hint.split(',')) {
    const at = pair.indexOf(':');
    if (at < 1) return null;
    const bound = pair.slice(0, at).trim();
    const px = Number(pair.slice(at + 1));
    if (!Number.isFinite(px) || px < 0 || px > MAX_RESERVE_PX) return null;
    if (bound === '*') return Math.round(px);
    const upTo = Number(bound);
    if (!Number.isFinite(upTo)) return null;
    if (usable <= upTo) return Math.round(px);
  }
  return null;
}

/** How much space to hold for this embed before its config has arrived. */
export function reserveFor(width: number, hint: string | null): number {
  return reserveFromHint(hint, width) ?? defaultReserve(width);
}

/**
 * The extra height a newsletter signup needs, held from the moment the config lands.
 *
 * The step appears on the completion screen, which is the one screen that arrives after the
 * article has been laid out around the embed. Reserving for it at the start means the box is
 * already tall enough when it appears, so nothing under the embed moves; reserving nothing would
 * push the rest of the article down at the exact moment the reader finished.
 *
 * The cost is a band of empty space under the questions on an interaction with a signup, which is
 * the same trade the reserve table makes and D25 describes. It is the sum of the parts: a heading,
 * a field, a consent line that wraps to two, a button, and the padding around them.
 */
export const SIGNUP_RESERVE_PX = 190;

const SAFE_FONT = /[^A-Za-z0-9 ,._'"-]/g;
const FALLBACK_FONT =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/**
 * A font family comes from publisher input and lands inside a CSS declaration, so anything that
 * could close the declaration or the rule is removed rather than escaped.
 */
export function safeFontFamily(input: string | undefined): string {
  if (input === undefined) return FALLBACK_FONT;
  const cleaned = input.replace(SAFE_FONT, '').trim();
  return cleaned.length === 0 ? FALLBACK_FONT : `${cleaned}, ${FALLBACK_FONT}`;
}

/** Hex to an rgba string, used for the tints derived from the text and accent colours. */
export function tint(hex: string, alpha: number): string {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return `rgba(0,0,0,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
}

export function buildStyles(p: LivePresentation, reservePx: number): string {
  const font = safeFontFamily(p.font_family);
  const radius = Math.min(40, Math.max(0, Math.round(p.corner_radius)));
  const inner = Math.max(0, radius - 4);
  const line = tint(p.text_colour, 0.14);
  const muted = tint(p.text_colour, 0.62);
  const track = tint(p.text_colour, 0.08);
  const accentSoft = tint(p.accent_colour, 0.1);

  return `
:host{display:block;contain:layout style}
.qz{all:initial;display:block;box-sizing:border-box;font-family:${font};font-size:16px;line-height:1.45;color:${p.text_colour};background:${p.background_colour};border:1px solid ${line};border-radius:${radius}px;padding:20px;min-height:${Math.max(0, Math.round(reservePx))}px;text-align:left}
.qz *,.qz *::before,.qz *::after{box-sizing:border-box}
.qz-t{margin:0 0 4px;font-size:14px;font-weight:600;letter-spacing:.02em;text-transform:uppercase;color:${p.accent_colour}}
.qz-i{margin:0 0 12px;font-size:14px;color:${muted}}
.qz-q{margin:0 0 16px;font-size:19px;font-weight:600;line-height:1.3}
.qz-opts{display:flex;flex-direction:column;gap:8px;margin:0;padding:0;list-style:none}
.qz-opt{display:block;width:100%;margin:0;padding:12px 14px;font:inherit;color:inherit;text-align:left;background:transparent;border:1px solid ${line};border-radius:${inner}px;cursor:pointer;transition:background .12s ease,border-color .12s ease}
.qz-opt:hover{background:${accentSoft};border-color:${p.accent_colour}}
.qz-opt:focus-visible{outline:2px solid ${p.accent_colour};outline-offset:2px}
.qz-opt[disabled]{cursor:default;opacity:.6}
.qz-res{display:flex;flex-direction:column;gap:10px;margin:0;padding:0;list-style:none}
.qz-row{position:relative;padding:10px 12px;border:1px solid ${line};border-radius:${inner}px;overflow:hidden}
.qz-row.qz-own{border-color:${p.accent_colour}}
.qz-fill{position:absolute;inset:0 auto 0 0;background:${track};border-radius:${inner}px 0 0 ${inner}px;transition:width .35s ease}
.qz-row.qz-own .qz-fill{background:${accentSoft}}
.qz-line{position:relative;display:flex;gap:12px;align-items:baseline;justify-content:space-between}
.qz-label{font-size:15px}
.qz-pct{font-variant-numeric:tabular-nums;font-weight:600;font-size:15px;white-space:nowrap}
.qz-row.qz-own .qz-pct{color:${p.accent_colour}}
.qz-you{margin-left:6px;font-size:12px;font-weight:600;color:${p.accent_colour};text-transform:uppercase;letter-spacing:.04em}
.qz-meta{margin:12px 0 0;font-size:13px;color:${muted}}
.qz-credit{margin:12px 0 0;font-size:11px;letter-spacing:.04em;text-transform:uppercase;color:${muted}}
.qz-opt.qz-sel{background:${accentSoft};border-color:${p.accent_colour}}
.qz-opt.qz-ok{border-color:${p.accent_colour};background:${accentSoft}}
.qz-opt.qz-no{border-color:${muted}}
.qz-mark{float:right;font-weight:700}
.qz-fb{margin:12px 0 0;font-size:14px;font-weight:600}
.qz-prog{display:flex;gap:10px;align-items:center;margin:0 0 12px;font-size:12px;color:${muted}}
.qz-track{flex:1;height:4px;border-radius:2px;background:${track};overflow:hidden}
.qz-grow{display:block;height:100%;background:${p.accent_colour};transition:width .2s ease}
.qz-nav{display:flex;gap:8px;align-items:center;margin:16px 0 0}
.qz-btn{margin:0;padding:10px 18px;font:inherit;font-weight:600;color:${p.background_colour};background:${p.accent_colour};border:1px solid ${p.accent_colour};border-radius:${inner}px;cursor:pointer}
.qz-btn:focus-visible,.qz-back:focus-visible{outline:2px solid ${p.accent_colour};outline-offset:2px}
.qz-btn[disabled]{cursor:default;opacity:.6}
.qz-back{margin:0;padding:10px 14px;font:inherit;color:${muted};background:transparent;border:1px solid ${line};border-radius:${inner}px;cursor:pointer}
.qz-text{display:block;width:100%;margin:0;padding:12px 14px;font:inherit;color:inherit;background:transparent;border:1px solid ${line};border-radius:${inner}px;resize:vertical}
.qz-text:focus-visible{outline:2px solid ${p.accent_colour};outline-offset:1px}
.qz-scale{display:flex;flex-wrap:wrap;gap:8px}
.qz-step{flex:1 1 44px;margin:0;padding:12px 4px;font:inherit;color:inherit;text-align:center;background:transparent;border:1px solid ${line};border-radius:${inner}px;cursor:pointer}
.qz-step.qz-sel{background:${accentSoft};border-color:${p.accent_colour}}
.qz-step:focus-visible{outline:2px solid ${p.accent_colour};outline-offset:2px}
.qz-ends{display:flex;justify-content:space-between;margin:6px 0 0;font-size:12px;color:${muted}}
.qz-err{margin:12px 0 0;font-size:14px;font-weight:600;color:${p.accent_colour}}
.qz-score{margin:0;font-size:28px;font-weight:700}
.qz-out{margin:0 0 8px;font-size:22px;font-weight:700}
.qz-img{display:block;width:100%;height:auto;margin:0 0 12px;border-radius:${inner}px}
.qz-consent{margin:14px 0 0;font-size:12px;line-height:1.4;color:${muted}}
.qz-consent a,.qz-su a{color:${p.accent_colour};text-decoration:underline}
.qz-consent a:focus-visible,.qz-su a:focus-visible{outline:2px solid ${p.accent_colour};outline-offset:2px}
.qz-su{margin:18px 0 0;padding:16px;border:1px solid ${line};border-radius:${inner}px}
.qz-su-h{margin:0 0 10px;font-size:16px;font-weight:600}
.qz-su-c{display:flex;gap:10px;align-items:flex-start;margin:12px 0 0;font-size:13px;line-height:1.4;color:${muted};cursor:pointer}
.qz-su-tick{flex:none;width:18px;height:18px;margin:1px 0 0;accent-color:${p.accent_colour};cursor:pointer}
.qz-su-tick:focus-visible{outline:2px solid ${p.accent_colour};outline-offset:2px}
.qz-su-hint{margin:10px 0 0;font-size:13px;color:${muted}}
.qz-su-done{margin:0;font-size:15px}
.qz-su .qz-btn{margin:12px 0 0}
.qz-su .qz-btn[aria-disabled='true']{opacity:.6}
.qz-sr{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0}
.qz-sk{display:flex;flex-direction:column;gap:8px}
.qz-bar{height:14px;border-radius:4px;background:${track};animation:qz-pulse 1.4s ease-in-out infinite}
.qz-bar.qz-w1{width:40%}
.qz-bar.qz-w2{width:75%;height:20px;margin-bottom:6px}
.qz-bar.qz-w3{height:42px}
@keyframes qz-pulse{0%,100%{opacity:1}50%{opacity:.5}}
@media (prefers-reduced-motion:reduce){.qz-bar{animation:none}.qz-fill,.qz-opt,.qz-grow{transition:none}}
`.trim();
}
